/**
 * Gallery Component for Dynamic Image Collections
 * 
 * A responsive, grid-based gallery component that automatically adapts to
 * different image orientations and provides an optimal viewing experience
 * across all device sizes.
 * 
 * Key Features:
 * - Automatic image orientation detection and layout
 * - Portrait images span two grid rows for better proportion
 * - Wide landscape images span two columns
 * - Responsive breakpoints (1/2/3 columns)
 * - Content-aware item ordering via Sanity CMS
 * - Precise height calculations for consistent grid alignment
 * 
 * Layout Intelligence:
 * - Portrait detection: height > width * 1.2 ratio
 * - Wide landscape: explicit CMS orientation setting
 * - Dynamic grid sizing based on content mix
 * - Gap-aware height calculations for perfect alignment
 * 
 * Responsive Behavior:
 * - Mobile (< 640px): Single column layout
 * - Tablet (640px-768px): Two column grid
 * - Desktop (> 768px): Three column grid
 * - Adaptive item sizing within each breakpoint
 * 
 * Performance Optimizations:
 * - Conditional rendering (no items = no output)
 * - Optimized sorting with stable references
 * - Delegated image handling to GalleryItem
 * - CSS Grid for efficient layout rendering
 * 
 * @param {Array} items - Array of gallery item objects from Sanity CMS
 * @param {string} items[].id - Unique identifier for each gallery item
 * @param {object} items[].image - Sanity image object
 * @param {string} items[].orientation - Layout orientation hint
 * @param {number} items[].order - Sort order for gallery arrangement
 * @param {object} items[].imageMetadata - Image dimensions and metadata
 * 
 * @description Responsive gallery with automatic layout optimization for mixed content
 */

import React from 'react';
import GalleryItem from './GalleryItem';

const Gallery = ({ items }) => {
  // If no items, don't render anything
  if (!items || items.length === 0) {
    return null;
  }

  // Sort items by order field if present
  const sortedItems = [...items].sort((a, b) => {
    if (a.order !== undefined && b.order !== undefined) {
      return a.order - b.order;
    }
    return 0;
  });

  // Calculate heights precisely to match grid spacing
  const imageHeight = 300; // Base height for regular images
  const gapSize = 16; // Reduced from 24px (gap-6) to 16px (gap-4)
  const portraitHeight = imageHeight * 2 + gapSize; // Exactly two images plus gap

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {sortedItems.map((item, index) => {
          // Determine if this is a portrait image
          const isPortrait = 
            (item.orientation === 'portrait') || 
            (item.imageMetadata?.dimensions && 
             item.imageMetadata.dimensions.height > item.imageMetadata.dimensions.width * 1.2);
          
          // Check if this is a wide landscape image (spans 2 columns)
          const isWideLandscape = item.orientation === 'wide-landscape';
          
          return (
            <div 
              key={item._id || index}
              className={`
                ${isPortrait ? 'row-span-2' : ''}
                ${isWideLandscape ? 'col-span-2' : ''}
              `}
              style={{ 
                height: isPortrait ? `${portraitHeight}px` : `${imageHeight}px`,
              }}
            >
              <GalleryItem 
                item={item} 
                allItems={sortedItems} 
                index={index} 
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Gallery; 